import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';
import type {
    ApiFlavor,
    ByoLlmCompatibilityProbeSummary,
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    ByoProductLlmConfiguration,
    LlmConnectorType,
} from './LlmGateway.types';

export function getByoProductConfigurations(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/products`;
    return get<Record<string, Record<string, ByoOperationGroupConfiguration>>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getByoProductLlmConfigurations(organizationId: string, tenantId: string, includeConnectionDetails: boolean = false) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations?includeConnectionDetails=${includeConnectionDetails}`;
    return get<ByoProductLlmConfiguration[]>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string, forceRefreshConnectionDetails: boolean = false) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}?forceRefreshConnectionDetails=${forceRefreshConnectionDetails}`;
    return get<ByoProductLlmConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function addByoProductLlmConfiguration(organizationId: string, tenantId: string, configuration: Partial<ByoProductLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations`;
    return post<ByoProductLlmConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function updateByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string, configuration: Partial<ByoProductLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}`;
    return put<void>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function deleteByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function validateByoProductLlmConfiguration(
    organizationId: string,
    tenantId: string,
    configuration: Partial<ByoProductLlmConfiguration>,
    configurationMode?: string
) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/validate`;
    const headers: Record<string, string> = { extendRequestHeaders: 'true' };

    if (configurationMode) {
        headers['X-UiPath-LlmGateway-ByoConfigMode'] = configurationMode;
    }

    return post<Record<string, ByoLlmCompatibilityProbeSummary>>(url, {
        ignoreBasePath: true,
        headers,
        body: configuration,
    });
}

export function getAvailableModelsForByoLlmConfiguration(organizationId: string, tenantId: string, configuration: Partial<ByoLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/available-models`;
    return post<string[]>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function getVendorToLlmsMappings(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/vendors/llms`;
    return get<Record<LlmConnectorType, string[]>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getVendorLlmApiFlavors(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/vendors/llms/api-flavors`;
    return get<Record<string, ApiFlavor[] | null>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}
